import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import StaticMode from "@mapbox/mapbox-gl-draw-static-mode";
import { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import { passing_draw_polygon as passingDrawPolygon } from "mapbox-gl-draw-passing-mode";
import { SnapLineMode } from "mapbox-gl-draw-snap-mode";

import { booleanIntersects, difference, featureCollection } from "fond/turf";
import { isAnyPolygon } from "fond/types/geojson";

import directSelectMode from "./modes/base/directSelect";
import drawPoint from "./modes/base/drawPoint";
import drawPolygon from "./modes/base/drawPolygon";
import polygonCutMode from "./modes/base/polygonCut";
import polygonSplitMode from "./modes/base/polygonSplit";
import simpleSelectMode from "./modes/base/simpleSelect";
import streetView from "./modes/base/streetView";

// Default snapping options used for drawing within market lens.
export const snappingOptions: Pick<MapboxDrawOptions, "snap" | "snapOptions" | "guides"> = {
  snap: true,
  snapOptions: {
    snapPx: 15,
    snapToMidPoints: false,
    snapVertexPriorityDistance: 0.0025,
  },
  guides: false,
};

/**
 * Validates that a feature (B) is either contained within, or intersects with with another feature (A).
 * @param featureA The feature.
 * @param featureB The feature being tested to see if it with within FeatureA
 */
export const isWithin = (featureA: Feature<Polygon | MultiPolygon>, featureB: Feature<Polygon | MultiPolygon>): boolean =>
  !difference(featureB, featureA) || booleanIntersects(featureA, featureB);

/**
 * Updates features within a feature collection.
 * Note: that the order of the features within the collection is not maintained.
 * @param collection the FeatureCollection that will be updated
 * @param features the features to be upserted into the collection
 */
export const updateFeatureCollection = (collection: FeatureCollection, features: Feature[]): FeatureCollection => {
  return featureCollection([...collection.features.filter((feature) => !features.some((feat) => feat.id === feature.id)), ...features]);
};

/**
 * Prevents the polygon overlapping any existing features.
 */
export const intersect = (polygon: Feature<Polygon | MultiPolygon>, all: Feature[]): Feature<Polygon | MultiPolygon> => {
  const newFeatures: Feature[] = [];
  let diffPoly: Feature<Polygon | MultiPolygon> = { ...polygon };

  all
    .filter(isAnyPolygon)
    .filter((feature) => feature.id !== polygon.id)
    .forEach((feature) => {
      if (isAnyPolygon(feature)) {
        const diff = difference(diffPoly, feature);
        if (diff) diffPoly = diff;
      }
      newFeatures.push(feature);
    });

  return { ...diffPoly, id: polygon.id };
};

/**
 * Clips a feature so that there is no overlap with any other features
 *
 * @param feature The feature to be clipped
 * @param all All features to be clipped against
 * @returns A clipped feature that has no overlap with any existing features
 */
export const clip = (feature: Feature<Polygon | MultiPolygon>, all: Feature[] = []): Feature<Polygon | MultiPolygon> => {
  return intersect(feature, all);
};

/**
 * Validates that polygon features have a turf "difference" when compared to the existing features.
 * In other words, the feature must not be completely contained by another feature.
 *
 * @param features A collection of features to validate
 * @param all A collection of existing features
 * @returns boolean indicating if all the features have a valid difference
 */
export const validateFeatures = (features: Feature[], all: Feature[] = []): boolean => {
  return features.every((newFeature) => {
    if (!newFeature.geometry) return false;

    if (isAnyPolygon(newFeature)) {
      return all
        .filter(({ id }) => id !== newFeature.id)
        .filter(isAnyPolygon)
        .every((feature) => difference(newFeature, feature));
    }

    return true;
  });
};

// A set of drawing modes that are always included, but can be overridden by modes passed via props.
export const baseDrawControlModes: Record<string, MapboxDraw.DrawCustomMode<any, any>> = {
  ...MapboxDraw.modes,
  draw_polygon: drawPolygon,
  draw_point: drawPoint,
  draw_line_string: SnapLineMode,
  direct_select: directSelectMode,
  passing_draw_polygon: passingDrawPolygon,
  ...polygonCutMode,
  ...polygonSplitMode,
  simple_select: simpleSelectMode,
  street_view: streetView,
  static: StaticMode,
};
